import { Link } from '../../../components/Link';
import { Icon } from '../../../components/icons';
import { PublicFooter } from '../../../components/PublicFooter';
import { PublicHeader } from '../../../components/PublicHeader';
import { breadcrumbSchema, Seo } from '../../../lib/seo';

interface ToolEntry {
  href: string;
  icon: React.ReactNode;
  title: string;
  body: string;
  /** Group rendered under a section heading on the hub. */
  group: 'checkers' | 'generators' | 'content' | 'diagnostics' | 'ai';
}

const TOOLS: ReadonlyArray<ToolEntry> = [
  // ── Checkers (verify what your domain already publishes) ─────────────
  {
    href: '/tools/domain-checker',
    icon: <Icon.ShieldCheck />,
    title: 'Domain health checker',
    body: 'Composite SPF + DKIM + DMARC + MX score for any domain. Same engine that powers our in-app Domain Health add-on.',
    group: 'checkers',
  },
  {
    href: '/tools/spf-checker',
    icon: <Icon.ShieldCheck />,
    title: 'SPF checker',
    body: "Verify your domain's SPF record + policy. Flags missing records, soft-fail vs hard-fail, and lookup-limit overflows.",
    group: 'checkers',
  },
  {
    href: '/tools/dkim-checker',
    icon: <Icon.Key />,
    title: 'DKIM checker',
    body: 'Probes 14 common DKIM selectors in parallel. Shows which selectors are publishing a signing key for your domain.',
    group: 'checkers',
  },
  {
    href: '/tools/dmarc-checker',
    icon: <Icon.ShieldAlert />,
    title: 'DMARC checker',
    body: 'Read your DMARC policy + percentage. Flags weak none / quarantine vs strict reject.',
    group: 'checkers',
  },
  {
    href: '/tools/blacklist-checker',
    icon: <Icon.Crosshair />,
    title: 'Email blacklist checker',
    body: 'Query 12+ public RBLs (Spamhaus, Barracuda, SpamCop, SORBS, …) in parallel for any IP or domain.',
    group: 'checkers',
  },

  // ── Generators (build records from scratch) ─────────────────────────
  {
    href: '/tools/dmarc-generator',
    icon: <Icon.Wand />,
    title: 'DMARC record generator',
    body: 'Build a valid DMARC TXT record with the right policy, pct%, and report addresses. Copy + paste into your DNS.',
    group: 'generators',
  },
  {
    href: '/tools/spf-generator',
    icon: <Icon.Wand />,
    title: 'SPF record generator',
    body: 'Pick the ESPs you authorise (Google, Microsoft 365, SendGrid, …) and the enforcement policy. Live DNS-lookup-limit counter.',
    group: 'generators',
  },
  {
    href: '/tools/dkim-generator',
    icon: <Icon.Wand />,
    title: 'DKIM key generator',
    body: 'Generate a 2048-bit RSA keypair in your browser via WebCrypto — the private key never leaves your device.',
    group: 'generators',
  },

  // ── Content (analyse the message itself) ────────────────────────────
  {
    href: '/tools/email-spam-checker',
    icon: <Icon.Mail />,
    title: 'Email spam checker',
    body: 'Score subject + body against ~15 deliverability rules. Returns 0-100 score + the exact rules to fix.',
    group: 'content',
  },
  {
    href: '/tools/spam-words-checker',
    icon: <Icon.Mail />,
    title: 'Spam words checker',
    body: 'Highlight every spam-trigger word and phrase in your draft inline, with severity weight + category breakdown.',
    group: 'content',
  },
  {
    href: '/tools/subject-line-tester',
    icon: <Icon.Type />,
    title: 'Email subject line tester',
    body: 'Grade your subject A–F on length, caps, emoji, punctuation, urgency, and spam triggers. Six dimensions, instant feedback.',
    group: 'content',
  },

  // ── Diagnostics (live protocol probes) ──────────────────────────────
  {
    href: '/tools/smtp-test',
    icon: <Icon.Server />,
    title: 'SMTP test tool',
    body: 'Open a TCP/TLS socket to any SMTP server, run EHLO / STARTTLS / MAIL FROM with the full transcript.',
    group: 'diagnostics',
  },

  // ── AI-powered (LLM-generated drafts) ───────────────────────────────
  {
    href: '/tools/ai-email-writer',
    icon: <Icon.Mail />,
    title: 'AI email writer',
    body: 'Tell us the goal — get a clean 4-6 sentence draft back. Cold outreach, follow-up, intro, reply, announcement.',
    group: 'ai',
  },
  {
    href: '/tools/subject-line-generator',
    icon: <Icon.Type />,
    title: 'Subject line generator',
    body: '7 distinct subject-line variants per call, in the 30-60 character sweet spot, with structure varied across the batch.',
    group: 'ai',
  },
  {
    href: '/tools/email-shortener',
    icon: <Icon.Wand />,
    title: 'Email shortener',
    body: 'Compress a verbose draft to 20-50% shorter. Preserves names, numbers, deadlines, and URLs verbatim.',
    group: 'ai',
  },
];

const GROUP_META: Record<ToolEntry['group'], { title: string; body: string }> = {
  checkers: {
    title: 'Checkers — verify what your domain already publishes',
    body: 'Pull live records, parse them, surface drift. Same engines as the paid in-app monitors.',
  },
  generators: {
    title: 'Generators — build records from scratch',
    body: 'Wizards for SPF, DKIM, DMARC. Copy the output straight into your DNS panel.',
  },
  content: {
    title: 'Content — score the message itself',
    body: 'Subject + body + word-level scoring. The other half of inbox placement, once the auth side is dialled in.',
  },
  diagnostics: {
    title: 'Diagnostics — live protocol probes',
    body: 'SMTP handshake with the full wire transcript. The "why isn\'t my mail sending" debugger.',
  },
  ai: {
    title: 'AI-powered — drafts in seconds',
    body: 'LLM-backed writing tools for cold outreach + follow-ups. Rate-limited per IP per day; signup gets you unlimited.',
  },
};

/**
 * Free public tools index — SEO landing page that lists every free tool grouped
 * by purpose. Each sub-page is a self-contained tool at its own URL with its
 * own SEO meta (per /tools/<slug>/index.html prerendered shell), so the index
 * itself only needs to be a clean directory.
 */
export function ToolsIndexPage() {
  const groups: ToolEntry['group'][] = ['checkers', 'generators', 'content', 'diagnostics', 'ai'];

  return (
    <>
      <Seo
        title="Free email deliverability tools"
        description="Free SPF, DKIM, DMARC checkers + generators, blacklist checker, spam-score checker, subject-line tester, SMTP test tool. No signup, no credit card."
        path="/tools"
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', url: 'https://sieveguard.com/' },
            { name: 'Tools', url: 'https://sieveguard.com/tools' },
          ]),
        ]}
      />
    <div className="mx-auto max-w-[1210px] overflow-x-clip px-6 pb-20">
      <PublicHeader />

      <section className="mx-auto max-w-3xl py-16 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Free tools · No signup
        </span>
        <h1 className="mt-6 font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
          Free email deliverability tools
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-muted">
          Fifteen focused tools for senders — checkers, generators, content scorers, live diagnostics,
          AI-powered drafting. Each on its own page. Rule-based tools are pure DNS / WebCrypto /
          in-house scoring; AI tools use the configured model with a per-IP daily cap.
        </p>
      </section>

      {groups.map((g) => (
        <section key={g} className="mt-6">
          <h2 className="font-display text-xl font-bold tracking-tight">{GROUP_META[g].title}</h2>
          <p className="mt-1 max-w-2xl text-[13px] text-muted">{GROUP_META[g].body}</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {TOOLS.filter((t) => t.group === g).map((t) => (
              <Link
                key={t.href}
                to={t.href}
                className="group rounded-2xl border border-bordersoft bg-panel p-6 transition hover:border-accentdeep hover:bg-panel2"
              >
                <span className="text-accent">{t.icon}</span>
                <h3 className="mt-3 text-base font-semibold">
                  {t.title} <span className="text-accent transition group-hover:translate-x-1">→</span>
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted">{t.body}</p>
              </Link>
            ))}
          </div>
        </section>
      ))}

      <section className="mt-16 rounded-2xl border border-bordersoft bg-gradient-to-b from-panel to-panel2 p-10 text-center">
        <h2 className="font-display text-2xl font-bold tracking-tight">
          Need more than 8 checks a day?
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted">
          Sign up for a workspace — unlimited Domain Health, Inbox Placement, Mailbox Warmup,
          Blacklist Monitoring, and Phone Validation runs across every email and domain you ingest.
        </p>
        <Link to="/register" className="mt-6 inline-block">
          <button className="h-12 rounded-lg bg-accent px-6 text-sm font-semibold text-black hover:bg-accentdeep">
            Create your workspace
          </button>
        </Link>
      </section>

      <PublicFooter />
    </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default ToolsIndexPage;
